import * as React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Form from '../../../components/Form';
import List from '../../../components/List';
import ClosableModal from '../../../components/Modal/ClosableModal';

import messages from '../messages';
import '../styles.css';

export const PublishChanges = ({ onClose, onDisregard, onNextAction, changeMessage }) => (
  <React.Fragment>
    <Modal.Header>
      <FormattedMessage {...changeMessage} />
    </Modal.Header>
    <Modal.Content>
      <FormattedMessage {...messages.publishModalContent} />
    </Modal.Content>
    <Modal.Actions>
      <div styleName="actions-container">
        <div>
          <Button id="publish-close-button" onClick={onClose}>
            <FormattedMessage {...messages.publishModalCloseButton} />
          </Button>
        </div>
        <div>
          <Button id="publish-continue-button" onClick={onDisregard}>
            <FormattedMessage {...messages.publishModalContinueButton} />
          </Button>
          <Button id="publish-next-button" primary onClick={onNextAction}>
            <FormattedMessage {...messages.publishModalPublishButton} />
          </Button>
        </div>
      </div>
    </Modal.Actions>
  </React.Fragment>
);

export const ConfirmModal = injectIntl(({
  onClose,
  intl,
  basicInfoChanges,
  hasMediaChanges,
  hasLinksAndProfilesChanges,
  hasIntroChanges,
}) => (
  <React.Fragment>
    <Modal.Header styleName="confirm-modal-header">
      <FormattedMessage {...messages.confirmationModalTitle} />
    </Modal.Header>
    <Modal.Content styleName="confirm-modal-content">
      <div styleName="confirm-modal-name">
        <FormattedMessage {...messages.confirmationModalApprovalDisclaimer} />
      </div>
      <div styleName="confirm-modal-hint">
        <strong><FormattedMessage {...messages.confirmationModalApprovalDisclaimerTime} /></strong>
      </div>
      <List bulleted styleName="confirm-modal-list">
        {basicInfoChanges.map(
          (change, index) => (
            <List.Item key={change} id={`list-basic-info-changes-${index}`}>{change}</List.Item>
          ),
        )}
        {hasIntroChanges && (
          <List.Item id="list-intro-changes">
            <FormattedMessage {...messages.confirmModalListIntro} />
          </List.Item>
        )}
        {hasMediaChanges && (
          <List.Item id="list-media-changes">
            <FormattedMessage {...messages.confirmModalListMedia} />
          </List.Item>
        )}
        {hasLinksAndProfilesChanges && (
          <List.Item id="list-links-and-profiles-changes">
            <FormattedMessage {...messages.confirmModalListLinksAndProfiles} />
          </List.Item>
        )}
      </List>
      <div>
        <FormattedMessage {...messages.confirmationModalJustifyChanges} />
      </div>
      <Form.TextArea
        required
        placeholder={intl.formatMessage(messages.confirmationModalTextboxPlaceholder)}
        styleName="confirm-modal-textarea"
      />
    </Modal.Content>
    <Modal.Actions>
      <Button id="confirm-close-button" onClick={onClose}>
        <FormattedMessage {...messages.confirmationModalCloseButton} />
      </Button>
      <Button id="confirm-submit-button" primary>
        <FormattedMessage {...messages.confirmationModalSubmitButton} />
      </Button>
    </Modal.Actions>
  </React.Fragment>
));

export const SuccessModal = injectIntl(({ onClose }) => (
  <React.Fragment>
    <Modal.Header styleName="success-modal-header">
      <FormattedMessage {...messages.successModalTitle} />
    </Modal.Header>
    <Modal.Content styleName="success-modal-content">
      <div styleName="success-modal-content-text">
        <FormattedMessage {...messages.successModalText} />
      </div>
      <div styleName="success-modal-link">
        <Button primary id="confirm-close-button" onClick={onClose}>
          <FormattedMessage {...messages.successModalButton} />
        </Button>
      </div>
    </Modal.Content>
  </React.Fragment>
));

export const SaveMask = () => (
  <div styleName="hubs-modal save-mask">
    <div className="hubs">
      <div styleName="bounceLoader">
        <div styleName="bounceLoaderChild bounceLoaderChild1 bounceLoaderChildAccent" />
        <div styleName="bounceLoaderChild bounceLoaderChild2 bounceLoaderChildAccent" />
        <div styleName="bounceLoaderChild bounceLoaderChild2 bounceLoaderChildAccent" />
      </div>
    </div>
  </div>
);

PublishChanges.propTypes = {
  changeMessage: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDisregard: PropTypes.func.isRequired,
  onNextAction: PropTypes.func.isRequired,
};

ConfirmModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  basicInfoChanges: PropTypes.arrayOf(PropTypes.string),
};
ConfirmModal.defaultProps = {
  basicInfoChanges: [],
};

SuccessModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

class PublishConfirmationModal extends React.PureComponent {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    onDisregard: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    initialStep: PropTypes.number,
    hasMediaChanges: PropTypes.bool,
    hasLinksAndProfilesChanges: PropTypes.bool,
    hasIntroChanges: PropTypes.bool,
    basicInfoChanges: PropTypes.arrayOf(PropTypes.string),
  }

  state = {
    confirmationStep: this.props.initialStep || 1,
  };

  componentDidMount() {
    if (this.props.initialStep === 3) {
      this.onSubmitData();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.open === true) {
      this.setState({ confirmationStep: nextProps.initialStep || 1 });
    }
  }

  onNextAction = () => {
    this.setState({ confirmationStep: 2 });
  }

  showSaveMask = () => {
    this.setState({ confirmationStep: 3 }, () => this.onSubmitData());
  }

  onSubmitData = async () => {
    await this.props.onSubmit();
  };

  render() {
    const {
      onClose,
      onDisregard,
      open,
      hasMediaChanges,
      hasLinksAndProfilesChanges,
      hasIntroChanges,
      basicInfoChanges,
    } = this.props;
    const { confirmationStep } = this.state;
    return (
      <React.Fragment>
        {confirmationStep !== 4 ? (
          <Modal as={Form} open={open} size="small" onSubmit={confirmationStep === 2 ? this.showSaveMask : null} data-cy="PremiumHEM-Publish-Modal">
            <React.Fragment>
              {confirmationStep === 1 ? (
                <PublishChanges
                  onClose={onClose}
                  onDisregard={onDisregard}
                  onNextAction={this.onNextAction}
                  changeMessage={
                    (hasMediaChanges && messages.publishMediaModalTitle) ||
                    (hasLinksAndProfilesChanges && messages.publishLinksAndProfilesModalTitle) ||
                    (hasIntroChanges && messages.publishIntroModalTitle) ||
                    (basicInfoChanges && messages.publishBasicInfoModalTitle) ||
                    messages.publishMediaModalTitle
                  }
                />
              ) : null}
              {confirmationStep === 2 && (
                <ConfirmModal
                  basicInfoChanges={basicInfoChanges}
                  hasMediaChanges={hasMediaChanges}
                  hasLinksAndProfilesChanges={hasLinksAndProfilesChanges}
                  hasIntroChanges={hasIntroChanges}
                  onClose={onClose}
                />
              )}
              {confirmationStep === 3 && <SaveMask />}
            </React.Fragment>
          </Modal>
        ) : (
          <ClosableModal as={Form} open={open} size="small" onClose={onClose} data-cy="PremiumHEM-Success-Modal">
            <SuccessModal onClose={onClose} />
          </ClosableModal>
        )}
      </React.Fragment>
    );
  }
}

export default PublishConfirmationModal;
